/**
 * Registering the offline shell (T79).
 *
 * The worker itself is `public/sw.js`. This is the half that lives in the app: when to register it,
 * and handing it the list of things the page already loaded so the *first* visit is enough to make
 * the game work offline.
 *
 * The decision of whether to register at all is a pure function, because the two cases that must
 * never register are both easy to get wrong by accident and impossible to notice once wrong:
 *
 *  - **Under the harness.** A worker that served a cached page to a test would make the test a
 *    statement about last week's build. Every e2e test boots with `?harness=1`.
 *  - **In development.** Vite serves modules unbundled and rewrites them on every edit; a cache in
 *    front of that turns "the change did nothing" into a debugging afternoon.
 */

export interface RegisterContext {
  /** `?harness=1` — the tests own time, randomness, and the network. */
  harness: boolean;
  /** A real build, i.e. not the dev server. */
  production: boolean;
  /** `'serviceWorker' in navigator`. Absent in some private-browsing modes, and that is fine. */
  supported: boolean;
}

export function shouldRegister(context: RegisterContext): boolean {
  return context.supported && context.production && !context.harness;
}

/** Where the worker lives, and the scope it needs: the whole origin. */
export const WORKER_URL = '/sw.js';

/**
 * Same-origin URLs the page has already fetched.
 *
 * The build content-hashes every asset name, so the worker cannot know them ahead of time and the
 * browser's own resource timeline is the only place the list exists. Sourcemaps are dropped — they
 * are large, and nobody debugging offline is doing it on a phone.
 */
export function loadedResources(view: {
  performance: { getEntriesByType(type: string): readonly { readonly name: string }[] };
  location: { origin: string };
}): string[] {
  return view.performance
    .getEntriesByType('resource')
    .map((entry) => entry.name)
    .filter((name) => name.startsWith(view.location.origin) && !name.endsWith('.map'));
}

/**
 * Register, then warm.
 *
 * Failure is swallowed on purpose. A worker that will not install is a degraded launch — the app
 * still runs, it just will not survive a cold start offline — and turning that into a thrown error
 * on the composition root would trade a small loss for a blank screen.
 */
export async function installOfflineShell(view: Window): Promise<void> {
  try {
    await view.navigator.serviceWorker.register(WORKER_URL);
    const ready = await view.navigator.serviceWorker.ready;
    const worker = view.navigator.serviceWorker.controller ?? ready.active;
    // On the very first visit the worker claims the page part-way through the load, so what got
    // there first never passed through its fetch handler. This is the fix for that.
    worker?.postMessage({ type: 'warm', urls: loadedResources(view) });
  } catch {
    // Nothing to do and nothing worth saying: the game is unaffected until the next cold start.
  }
}
